/**
 * InventoryService — CRUD for vendor product inventory.
 * Stock items, movements and config: the planning API is the only source of truth.
 * Vendors/Products/Prices/Snapshots: local WhatsApp-brief data; no backend endpoint yet.
 */

import {
  InventoryVendor,
  InventoryProduct,
  PriceEntry,
  InventorySnapshot,
  INVENTORY_VENDORS_KEY,
  INVENTORY_PRODUCTS_KEY,
  INVENTORY_PRICES_KEY,
  INVENTORY_SNAPSHOTS_KEY,
} from '../types/Inventory';
import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';

// ── Helpers ──────────────────────────────────────────────────────────────────

function load<T>(key: string): T[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

function save<T>(key: string, data: T[]): void {
  localStorage.setItem(key, JSON.stringify(data));
}

function uuid(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ── Vendors ──────────────────────────────────────────────────────────────────

export function loadInventoryVendors(): InventoryVendor[] {
  return load<InventoryVendor>(INVENTORY_VENDORS_KEY);
}

export function saveInventoryVendor(
  data: Omit<InventoryVendor, 'id' | 'createdAt' | 'updatedAt'>
): InventoryVendor {
  const vendors = loadInventoryVendors();
  const now = new Date().toISOString();
  const vendor: InventoryVendor = { ...data, id: uuid(), createdAt: now, updatedAt: now };
  save(INVENTORY_VENDORS_KEY, [...vendors, vendor]);
  return vendor;
}

export function updateInventoryVendor(
  id: string,
  data: Partial<InventoryVendor>
): InventoryVendor | null {
  const vendors = loadInventoryVendors();
  const idx = vendors.findIndex(v => v.id === id);
  if (idx === -1) return null;
  const updated = { ...vendors[idx], ...data, updatedAt: new Date().toISOString() };
  save(INVENTORY_VENDORS_KEY, vendors.map((v, i) => i === idx ? updated : v));
  return updated;
}

export function deleteInventoryVendor(id: string): void {
  save(INVENTORY_VENDORS_KEY, loadInventoryVendors().filter(v => v.id !== id));
  // Also remove all products for this vendor
  const allProducts = load<InventoryProduct>(INVENTORY_PRODUCTS_KEY);
  save(INVENTORY_PRODUCTS_KEY, allProducts.filter(p => p.vendorId !== id));
}

// ── Products ─────────────────────────────────────────────────────────────────

export function loadInventoryProducts(vendorId?: string): InventoryProduct[] {
  const all = load<InventoryProduct>(INVENTORY_PRODUCTS_KEY);
  return vendorId ? all.filter(p => p.vendorId === vendorId) : all;
}

export function upsertProduct(
  data: Omit<InventoryProduct, 'id' | 'lastUpdated'>
): InventoryProduct {
  const products = loadInventoryProducts();
  const existing = products.find(
    p => p.vendorId === data.vendorId && p.productCode === data.productCode
  );
  const now = new Date().toISOString();

  if (existing) {
    const updated = { ...existing, ...data, lastUpdated: now };
    save(INVENTORY_PRODUCTS_KEY, products.map(p => p.id === existing.id ? updated : p));
    return updated;
  }

  const product: InventoryProduct = { ...data, id: uuid(), lastUpdated: now };
  save(INVENTORY_PRODUCTS_KEY, [...products, product]);
  return product;
}

// ── Prices ───────────────────────────────────────────────────────────────────

export function loadPrices(productId?: string): PriceEntry[] {
  const all = load<PriceEntry>(INVENTORY_PRICES_KEY);
  return productId ? all.filter(p => p.productId === productId) : all;
}

export function addPriceEntry(
  data: Omit<PriceEntry, 'id'>
): PriceEntry {
  const prices = loadPrices();
  // Close off any existing valid price for this product+vendorLabel
  const updated = prices.map(p =>
    p.productId === data.productId && p.vendorLabel === data.vendorLabel && !p.validTo
      ? { ...p, validTo: data.validFrom }
      : p
  );
  const entry: PriceEntry = { ...data, id: uuid() };
  save(INVENTORY_PRICES_KEY, [...updated, entry]);
  return entry;
}

export function getCurrentPrices(productId: string): PriceEntry[] {
  return loadPrices(productId).filter(p => !p.validTo);
}

// ── Snapshots ────────────────────────────────────────────────────────────────

export function saveSnapshot(
  data: Omit<InventorySnapshot, 'id'>
): InventorySnapshot {
  const snapshots = load<InventorySnapshot>(INVENTORY_SNAPSHOTS_KEY);
  const snapshot: InventorySnapshot = { ...data, id: uuid() };

  // Apply snapshot to products
  data.products.forEach(item => {
    const product = upsertProduct({
      vendorId: data.vendorId,
      productCode: item.productCode,
      name: item.productCode,
      category: 'fabric',
      available: item.available,
      notes: item.notes,
      updatedBy: data.source,
    });

    if (item.priceNgn && item.vendorLabel) {
      addPriceEntry({
        productId: product.id,
        vendorId: data.vendorId,
        priceNgn: item.priceNgn,
        vendorLabel: item.vendorLabel,
        validFrom: data.takenAt,
        source: data.source === 'whatsapp_brief' ? `WhatsApp brief ${data.takenAt.slice(0, 10)}` : 'manual',
      });
    }
  });

  save(INVENTORY_SNAPSHOTS_KEY, [...snapshots, snapshot]);
  return snapshot;
}


// ── Stock ledger (API) ───────────────────────────────────────────────────────
//
// Items carry an on-hand `stock` that is the sum of the ledger (movements);
// stock changes only by a movement: receiving against an order (written by the
// server) or an adjustment made here. Money is a decimal string beside its
// currency, null for a contractor. A failed call throws; nothing is swallowed.

export type MovementType = 'received' | 'allocated' | 'adjusted' | 'returned' | 'damaged' | 'lost' | 'opening';
export type AdjustMovementType = 'adjusted' | 'damaged' | 'lost' | 'returned' | 'opening';

export interface InventoryItem {
  id: string;
  name: string;
  item_type: string | null;
  category: string | null;
  description: string | null;
  image: string | null;
  unit_price: string | null;
  currency: string | null;
  stock: number | string;
  pack_size?: number | string | null;
  vendor: string | null;
  notes: string | null;
  inventory_status: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  last_movement_at: string | null;
}

export interface StockMovement {
  id: string;
  item_id: string;
  quantity_delta: number | string;
  unit: string | null;
  pool: string | null;
  movement_type: MovementType;
  reference_type: string | null;
  reference_id: string | null;
  notes: string | null;
  occurred_at: string;
  order_line_id: string | null;
}

export interface InventoryItemInput {
  name: string;
  item_type?: string | null;
  category?: string | null;
  description?: string | null;
  unit_price?: string | null;
  currency?: string | null;
  pack_size?: number | string | null;
  vendor?: string | null;
  notes?: string | null;
}

interface Envelope<T> { success: boolean; data: T; meta?: { total: number; page: number; limit: number }; error?: string }

async function unwrap<T>(promise: Promise<Envelope<T>>): Promise<T> {
  const res = await promise;
  if (!res || res.success === false) throw new Error(res?.error || 'The request did not go through.');
  return res.data;
}

const enc = encodeURIComponent;
let itemsCache: InventoryItem[] = [];

/** The last list read from the API (a warm start for global search); empty until a load lands. */
export function loadInventoryItems(): InventoryItem[] {
  return itemsCache;
}

export async function fetchInventoryItems(opts: { lifecycle?: 'active' | 'archived' | 'all' } = {}): Promise<InventoryItem[]> {
  const q = opts.lifecycle ? `?lifecycle=${opts.lifecycle}` : '';
  const rows = await unwrap(apiGet<Envelope<InventoryItem[]>>(planningPath(`/inventory/items${q}`)));
  const list = Array.isArray(rows) ? rows : [];
  if (!opts.lifecycle || opts.lifecycle === 'active') itemsCache = list;
  return list;
}

export const createInventoryItem = (input: InventoryItemInput) =>
  unwrap(apiPost<Envelope<InventoryItem>>(planningPath('/inventory/items'), input));

export const updateInventoryItem = (id: string, input: Partial<InventoryItemInput>) =>
  unwrap(apiPut<Envelope<InventoryItem>>(planningPath(`/inventory/items/${enc(id)}`), input));

export const archiveInventoryItem = (id: string) =>
  unwrap(apiPost<Envelope<InventoryItem>>(planningPath(`/inventory/items/${enc(id)}/archive`), {}));

export const restoreInventoryItem = (id: string) =>
  unwrap(apiPost<Envelope<InventoryItem>>(planningPath(`/inventory/items/${enc(id)}/restore`), {}));

export const fetchMovements = (itemId: string) =>
  unwrap(apiGet<Envelope<StockMovement[]>>(planningPath(`/inventory/items/${enc(itemId)}/movements`)));

/** Adjust stock by a signed, non-zero amount. Taking stock below zero is refused by the server (400 negative_stock). */
export const adjustStock = (
  itemId: string,
  input: { quantity_delta: string | number; movement_type: AdjustMovementType; notes?: string; pool?: string; idempotency_key?: string },
) => unwrap(apiPost<Envelope<{ item: InventoryItem; movement: StockMovement }>>(planningPath(`/inventory/items/${enc(itemId)}/adjust`), input));

export const INVENTORY_REFRESHED_EVENT = 'inventory:refreshed';

/**
 * The auto-refresh's loader for stock items. It rejects on failure so the sync
 * hook can log it; it never shows a toast. Open Inventory screens re-read via the event.
 */
export async function refreshInventoryFromApi(): Promise<InventoryItem[]> {
  const items = await fetchInventoryItems({ lifecycle: 'active' });
  window.dispatchEvent(new CustomEvent(INVENTORY_REFRESHED_EVENT, { detail: { items } }));
  return items;
}

export function __resetCacheForTests(): void {
  itemsCache = [];
}
